/**
 * CF7フォームの共通フロー制御（contact / audition / form で共有）
 *
 * 「送信する」は2段階。1回目で検証を通したら確認フェーズへ移り、フォームの先頭へ戻して
 * 利用者に読み直させ、2回目で実際に送信する。確認フェーズでは入力欄を隠して、各行に
 * 入力内容の写しだけを出す。直すときは「入力画面に戻る」で入力欄へ戻す。
 * 入力欄そのものは隠すだけで残すので、送信されるのは元の入力欄の値。
 *
 * バリデーション規則はJS側に持たず、CF7が出力する `wpcf7-validates-as-*` から読み取る。
 * フォーム項目を管理画面で足しても、このファイルを触らずに済む。
 */

import { getLenis } from './lib-smooth-scroll'
import { playReveal } from './cmn-reveal'

type Rule = {
    /** 検証NGのときに出すメッセージ */
    message: string
    /** 値がルールを満たすか */
    test: (value: string) => boolean
}

const REQUIRED_MESSAGE = '入力して下さい。'
const SELECT_MESSAGE = '選択して下さい。'
const FORMAT_MESSAGE = '正しい形式で入力して下さい。'
const AGREE_MESSAGE = '個人情報の取り扱いに同意して下さい。'
const UPLOAD_MIN_MESSAGE = (min: number): string => `写真を${min}枚以上選んで下さい。`
const EMPTY_PREVIEW = '入力なし'
const PREVIEW = 'Formbody__mainFieldsRowPreview'

/** 見出しを入れ替えるとき、いまの見出しを消すのにかける時間（ミリ秒） */
const TITLE_OUT_MS = 300

const FORMAT_RULES: Array<{ flag: string; rule: Rule }> = [
    {
        flag: 'wpcf7-validates-as-email',
        rule: { message: 'メールアドレスの形式で入力して下さい。', test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) },
    },
    {
        flag: 'wpcf7-validates-as-url',
        rule: { message: 'https:// から始まるURLを入力して下さい。', test: (v) => /^https?:\/\/\S+$/.test(v) },
    },
    {
        flag: 'wpcf7-validates-as-tel',
        rule: { message: '電話番号を入力して下さい。', test: (v) => /^[0-9-]+$/.test(v) },
    },
]

/**
 * 対象まで滑らかに送る
 *
 * @description 慣性が動いているあいだ scrollIntoView は届かないことがある。Lenisは
 *              自分が動かしている最中のネイティブのスクロールを自分の目標値で上書きするので、
 *              直前にホイールを回していたかどうかで成否が変わる。慣性が居るときは慣性に頼む
 * @param el 送り先
 * @param center 画面の中央に置くか。false なら上端に寄せる
 */
const scrollToEl = (el: HTMLElement, center: boolean): void => {
    const lenis = getLenis()

    if (lenis && !lenis.isStopped) {
        // Lenisは要素を渡すと scroll-margin-top のぶん下げて止める。上端に寄せるときはそれが
        // ちょうどヘッダー避けになるが、中央に置くときは余計なので offset で打ち消す
        const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0
        const gap = window.innerHeight - el.getBoundingClientRect().height
        const offset = center ? margin - Math.max(0, gap / 2) : 0

        lenis.scrollTo(el, { offset, lock: true, force: true })
        return
    }

    el.scrollIntoView({ behavior: 'smooth', block: center ? 'center' : 'start' })
}

/**
 * 入力欄が属するフィールド行（エラー表示の受け皿）を返す
 * @param el 入力欄
 * @returns フィールド行。見つからなければ null
 */
const getRow = (el: Element): HTMLElement | null =>
    el.closest<HTMLElement>('.Formbody__mainFieldsRow')

/**
 * 表示中のエラーをすべて消す
 * @param form CF7のform要素
 */
const clearErrors = (form: HTMLFormElement): void => {
    form.querySelectorAll<HTMLElement>('.Formbody__mainFieldsRow').forEach(clearRowError)
}

/**
 * フィールド行1つぶんのエラーを消す
 * @param row フィールド行
 */
const clearRowError = (row: HTMLElement): void => {
    row.classList.remove('hasError')
    row.querySelector('.Formbody__mainFieldsRowError')?.remove()
    row.querySelectorAll('input, select, textarea').forEach((el) => {
        el.removeAttribute('aria-describedby')
        el.removeAttribute('aria-invalid')
    })
}

/**
 * フィールド行にエラーメッセージを出し、支援技術向けに入力欄と関連付ける
 * @param row フィールド行
 * @param message 表示するメッセージ
 */
const showError = (row: HTMLElement, message: string): void => {
    row.classList.add('hasError')

    // 1行につきエラーは1件だけ。先に出たものを優先する
    if (row.querySelector('.Formbody__mainFieldsRowError')) return

    const controls = row.querySelectorAll<HTMLElement>('.wpcf7-form-control-wrap input, .wpcf7-form-control-wrap select, .wpcf7-form-control-wrap textarea')
    const id = `ov-err-${controls[0]?.getAttribute('name') ?? Math.random().toString(36).slice(2)}`

    const span = document.createElement('span')
    span.className = 'Formbody__mainFieldsRowError'
    span.id = id
    span.setAttribute('role', 'alert')
    span.textContent = message
    row.appendChild(span)

    controls.forEach((el) => {
        el.setAttribute('aria-describedby', id)
        el.setAttribute('aria-invalid', 'true')
    })
}

/**
 * 1つの入力欄を検証する
 * @param el 入力欄
 * @returns NGならメッセージ、OKなら null
 */
const validateControl = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | null => {
    const required = el.classList.contains('wpcf7-validates-as-required') || el.required

    // アップロード欄は枠を並べて複数選択にしてあるので、枠ごとではなく枚数で見る。
    // 枠ごとに「入力して下さい。」を出すと、何枚足りないのか利用者に伝わらない
    const box = el.type === 'file' ? el.closest<HTMLElement>('[data-upload]') : null

    if (box) {
        const min = Number(box.dataset.uploadMin ?? '0')

        if (min <= 0) return null

        const picked = [...box.querySelectorAll<HTMLInputElement>('input[type="file"]')].filter(
            (input) => (input.files?.length ?? 0) > 0
        ).length

        return picked < min ? UPLOAD_MIN_MESSAGE(min) : null
    }

    if (el.type === 'checkbox') {
        // CF7の [acceptance] は必須でも wpcf7-validates-as-required が付かないので、
        // optional 指定が無いものは必須として扱う
        const acceptance = el.closest('.wpcf7-acceptance')
        const mustCheck = required || (!!acceptance && !acceptance.classList.contains('wpcf7-optional'))

        return mustCheck && !(el as HTMLInputElement).checked ? AGREE_MESSAGE : null
    }

    const value = el.value.trim()

    if (required && value === '') {
        return el.tagName === 'SELECT' ? SELECT_MESSAGE : REQUIRED_MESSAGE
    }

    if (value === '') return null

    const format = FORMAT_RULES.find(({ flag }) => el.classList.contains(flag))

    return format && !format.rule.test(value) ? format.rule.message : null
}

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const CONTROLS = '.wpcf7-form-control-wrap input, .wpcf7-form-control-wrap select, .wpcf7-form-control-wrap textarea'

/**
 * 1行ぶんを、利用者が触った入力欄だけで検証し直す
 *
 * @description 入力の途中で出すエラー用。まだ触っていない欄まで見ると、生年月日の「年」を
 *              選んだだけで「月」「日」に「選択して下さい。」が出てしまう
 * @param row フィールド行
 * @param touched 触ったことのある入力欄
 * @param checkGroup 「どれか1つは必須」の組も判定するか
 */
const validateRow = (row: HTMLElement, touched: WeakSet<Element>, checkGroup: boolean): void => {
    const group = groupOf(row)
    const rows = group ? group.rows : [row]

    rows.forEach((r) => {
        clearRowError(r)

        r.querySelectorAll<Control>(CONTROLS).forEach((el) => {
            if (!touched.has(el)) return
            if (el.type === 'radio' && r.querySelector(`input[name="${el.name}"]:checked`)) return

            const message = validateControl(el)
            if (message) showError(r, message)
        })
    })

    if (group && checkGroup && groupEmpty(group.rows)) showError(group.rows[0], group.message)
}

type Group = {
    /** 組に入る行。エラーは先頭の行に出す */
    rows: HTMLElement[]
    /** どれも未入力のときに出すメッセージ */
    message: string
}

/**
 * 「どれか1つは必須」の組を返す
 *
 * @description CF7は組での必須を書けないので、フォームの本文で行に data-require-one="組の名前" を付けて束ねる。
 *              メッセージは組の先頭の行の data-require-one-message に置く
 * @param row フィールド行
 * @returns 行が属する組。属していなければ null
 */
const groupOf = (row: HTMLElement): Group | null => {
    const key = row.dataset.requireOne
    const form = row.closest('form')

    if (!key || !form) return null

    const rows = [...form.querySelectorAll<HTMLElement>(`[data-require-one="${CSS.escape(key)}"]`)]

    return { rows, message: rows[0].dataset.requireOneMessage ?? REQUIRED_MESSAGE }
}

/**
 * 組の欄がどれも未入力か
 * @param rows 組に入る行
 * @returns どれも未入力なら true
 */
const groupEmpty = (rows: HTMLElement[]): boolean =>
    rows.every((r) => [...r.querySelectorAll<Control>(CONTROLS)].every((el) => el.value.trim() === ''))

/**
 * フォーム全体を検証し、最初にNGだったフィールド行を返す
 * @param form CF7のform要素
 * @returns 最初のエラー行。全部OKなら null
 */
const validate = (form: HTMLFormElement): HTMLElement | null => {
    form
        .querySelectorAll<Control>(CONTROLS)
        .forEach((el) => {
            // ラジオは同名グループで1回だけ見る
            if (el.type === 'radio' && form.querySelector(`input[name="${el.name}"]:checked`)) return

            const message = validateControl(el)
            if (!message) return

            const row = getRow(el)
            if (row) showError(row, message)
        })

    form.querySelectorAll<HTMLElement>('[data-require-one]').forEach((row) => {
        const group = groupOf(row)

        if (group && group.rows[0] === row && groupEmpty(group.rows)) showError(row, group.message)
    })

    // 組のエラーは後から足すので、画面の上から最初の行を拾い直す
    return form.querySelector<HTMLElement>('.Formbody__mainFieldsRow.hasError')
}

/**
 * 入力欄1つぶんの、見せるための値を返す
 *
 * @param wrap CF7が入力欄を囲むspan
 * @returns 入力された値。未入力なら空文字
 */
const valueOf = (wrap: Element): string => {
    const select = wrap.querySelector('select')

    if (select) return select.value === '' ? '' : (select.selectedOptions[0]?.textContent ?? '').trim()

    // ラジオ・チェックボックスは選ばれた項目の文言を並べる
    const checked = [...wrap.querySelectorAll<HTMLInputElement>('input[type="radio"], input[type="checkbox"]')].filter((el) => el.checked)

    if (wrap.querySelector('input[type="radio"], input[type="checkbox"]')) {
        return checked
            .map((el) => el.closest('.wpcf7-list-item')?.querySelector('.wpcf7-list-item-label')?.textContent?.trim() ?? el.value)
            .join('、')
    }

    const field = wrap.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')

    return field ? field.value.trim() : ''
}

/**
 * 写しの行を作る
 *
 * @param text 見せる値。空なら「入力なし」
 * @returns 写しの要素
 */
const previewText = (text: string): HTMLElement => {
    const p = document.createElement('p')
    p.className = PREVIEW
    p.textContent = text === '' ? EMPTY_PREVIEW : text

    return p
}

/**
 * 小項目の並ぶ行を、SP用に1つの写しへまとめる
 *
 * @description PCは小項目ごとに見出しを残すが、SPのデザインは行の見出しの下に
 *              「TikTok：URL」を1つずつ改行して並べる。未入力の小項目は出さない
 * @param subs 小項目の箱
 * @returns 写しの要素
 */
const previewSum = (subs: HTMLElement[]): HTMLElement => {
    const parts = subs.flatMap((sub) => {
        const wrap = sub.querySelector('.wpcf7-form-control-wrap')
        const value = wrap ? valueOf(wrap) : ''

        if (value === '') return []

        const label = sub.querySelector('.Formbody__mainFieldsRowSublabel')?.textContent?.trim() ?? ''

        return [`${label}：${value}`]
    })

    const p = previewText(parts.join('\n'))
    p.classList.add('type-sum', 'type-multi')

    return p
}

/**
 * アップロード欄の写しを作る
 *
 * @description 選んだ写真の並びは cmn-upload.ts が描いているので、枠ごと複製して
 *              選ぶための部品だけ落とす。入力欄まで複製すると同じ名前の欄が2つになり、送信に混ざる
 * @param box アップロード欄の枠
 * @returns 写しの要素
 */
const previewUpload = (box: HTMLElement): HTMLElement => {
    const copy = box.cloneNode(true) as HTMLElement

    copy.querySelectorAll('.wpcf7-form-control-wrap, [data-upload-pick], [data-upload-del], .Formbody__mainFieldsRowUploadLead, .type-error').forEach((el) => el.remove())
    copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'))
    copy.removeAttribute('data-upload')
    copy.classList.remove('is-drag', 'is-error', 'is-full')
    copy.classList.add(PREVIEW, 'type-upload')

    if (!copy.querySelector('.Formbody__mainFieldsRowUploadListItem')) {
        copy.textContent = ''
        copy.appendChild(previewText(''))
    }

    return copy
}

/**
 * 生年月日のように、欄と「年」「月」を交互に並べた項目を1つの値にまとめる
 *
 * @param date 欄を並べた箱
 * @returns まとめた値。どれも未入力なら空文字
 */
const valueOfDate = (date: HTMLElement): string => {
    const wraps = [...date.querySelectorAll('.wpcf7-form-control-wrap')]

    if (wraps.every((wrap) => valueOf(wrap) === '')) return ''

    return [...date.children]
        .map((el) => (el.classList.contains('wpcf7-form-control-wrap') ? valueOf(el) : (el.textContent ?? '').trim()))
        .join('')
}

/**
 * 確認フェーズの写しを各行に置く
 *
 * @description 行の見出しはそのまま使い、入力欄はCSSで隠す。同意書の行は本文と
 *              チェックを隠し、同意した旨の1行だけを見せる
 * @param form CF7のform要素
 */
const buildPreviews = (form: HTMLFormElement): void => {
    form.querySelectorAll<HTMLElement>('.Formbody__mainFieldsRow').forEach((row) => {
        const agree = row.querySelector<HTMLElement>('.Formbody__mainFieldsRowTermsAgree')

        // 確認フェーズは同意できていないと来られないので、チェックの文言をそのまま写す
        if (agree) {
            const preview = previewText(agree.querySelector('.wpcf7-list-item-label')?.textContent?.trim() ?? '')
            preview.classList.add('type-terms')
            row.appendChild(preview)
            return
        }

        const box = row.querySelector<HTMLElement>('[data-upload]')

        if (box) {
            row.appendChild(previewUpload(box))
            return
        }

        const subs = row.querySelectorAll<HTMLElement>('.Formbody__mainFieldsRowSub')

        if (subs.length) {
            subs.forEach((sub) => {
                const wrap = sub.querySelector('.wpcf7-form-control-wrap')
                sub.appendChild(previewText(wrap ? valueOf(wrap) : ''))
            })
            row.appendChild(previewSum([...subs]))
            return
        }

        const date = row.querySelector<HTMLElement>('.Formbody__mainFieldsRowDate')

        if (date) {
            row.appendChild(previewText(valueOfDate(date)))
            return
        }

        const wrap = row.querySelector('.wpcf7-form-control-wrap')

        if (!wrap) return

        const preview = previewText(valueOf(wrap))

        // 複数行の値は行送りを入力欄の文字組みに合わせる
        if (wrap.querySelector('textarea')) preview.classList.add('type-multi')

        row.appendChild(preview)
    })
}

/**
 * 写しを片付けて入力欄に戻す
 * @param form CF7のform要素
 */
const clearPreviews = (form: HTMLFormElement): void => {
    form.querySelectorAll(`.${PREVIEW}`).forEach((el) => el.remove())
}

/**
 * ページの見出しを入力／確認で入れ替える仕掛けを作る
 *
 * @description いまの見出しを薄くして消してから、次の見出しを字形の出方で出す。
 *              続けて切り替えられたら、消している途中のものは元へ戻して次の指示に従う
 * @param root [data-form-flow] を持つルート要素
 * @returns 出したい見出しのキー（input / confirm）を受け取る関数
 */
const titleSwitcher = (root: HTMLElement): ((key: string) => void) => {
    const titles = [...root.querySelectorAll<HTMLElement>('[data-form-ttl]')]
    // 字形は読み上げから外してあり、h1の読みはこの見えない文字が持つ
    const label = root.querySelector<HTMLElement>('[data-form-ttl-label]')
    let fading: Animation | null = null

    return (key) => {
        const next = titles.find((el) => el.dataset.formTtl === key)

        if (!next) return

        fading?.cancel()
        fading = null

        const cur = titles.find((el) => !el.hidden && el !== next)

        /**
         * 次の見出しだけを残して出し直す
         */
        const reveal = (): void => {
            titles.forEach((el) => {
                el.hidden = el !== next
            })
            if (label && next.dataset.label) label.textContent = next.dataset.label
            playReveal(next)
        }

        if (!cur) {
            if (next.hidden) reveal()
            return
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            reveal()
            return
        }

        const anim = cur.animate([{ opacity: 1 }, { opacity: 0 }], { duration: TITLE_OUT_MS, easing: 'ease-out', fill: 'forwards' })

        fading = anim
        anim.onfinish = () => {
            fading = null
            reveal()
            // 薄くした指定は残さない。次に戻ってきたときに消えたまま出てしまう
            anim.cancel()
        }
    }
}

/**
 * 1つのフォームを初期化する
 * @param root [data-form-flow] を持つルート要素
 */
const initForm = (root: HTMLElement): void => {
    const form = root.querySelector<HTMLFormElement>('form.wpcf7-form')
    const submitBtn = root.querySelector<HTMLButtonElement>('[data-action="submit"]')
    const backBtn = root.querySelector<HTMLButtonElement>('[data-action="back"]')
    const realSubmit = form?.querySelector<HTMLInputElement>('input[type="submit"], button[type="submit"]')
    const steps = root.querySelectorAll<HTMLElement>('.Formstep__item')
    const thanksUrl = root.dataset.thanks ?? '/'

    if (!form || !submitBtn || !realSubmit) return

    const cf7Id = form.querySelector<HTMLInputElement>('input[name="_wpcf7"]')?.value ?? ''
    const btnTxt = submitBtn.querySelector<HTMLElement>('.Formbtn__innerTxt')
    // 文言はPHP側に置く。入力フェーズが label、確認フェーズが data-label-alt
    const labelInput = btnTxt?.textContent ?? ''
    const labelConfirm = submitBtn.dataset.labelAlt ?? labelInput
    let confirming = false
    const showTitle = titleSwitcher(root)
    const leads = root.querySelectorAll<HTMLElement>('[data-form-lead]')

    /**
     * 入力／確認フェーズを切り替え、ステップ表示とボタンのラベルを合わせる
     * @param next 確認フェーズにするか
     */
    const setPhase = (next: boolean): void => {
        if (next !== confirming) {
            clearPreviews(form)
            if (next) buildPreviews(form)
            showTitle(next ? 'confirm' : 'input')
            leads.forEach((el) => {
                el.hidden = el.dataset.formLead !== (next ? 'confirm' : 'input')
            })
        }

        confirming = next
        root.classList.toggle('is-confirm', next)

        if (btnTxt) btnTxt.textContent = next ? labelConfirm : labelInput

        steps.forEach((step) => {
            const key = step.dataset.step
            if (key !== 'input' && key !== 'confirm') return
            step.classList.toggle('is-current', key === (next ? 'confirm' : 'input'))
        })
    }

    /**
     * 送信の連打防止を解除する
     */
    const unlock = (): void => {
        submitBtn.disabled = false
    }

    // その場の検証。欄を離れたときに出し、出ている行は打つたびに見直して直ったら消す
    const touched = new WeakSet<Element>()

    /**
     * イベントの出どころがCF7の入力欄なら、それと行を返す
     * @param e イベント
     * @returns 入力欄とフィールド行。対象外なら null
     */
    const targetOf = (e: Event): { el: Control; row: HTMLElement } | null => {
        const el = e.target as Element | null
        if (!el?.matches(CONTROLS)) return null

        const row = getRow(el)
        return row ? { el: el as Control, row } : null
    }

    form.addEventListener('focusout', (e) => {
        const t = targetOf(e)
        if (!t) return

        touched.add(t.el)

        // 組の判定は組の外へ出たときだけ。Instagramを空で離れた時点で出すと、
        // TikTokに書くつもりで次の欄へ移っただけの人にもエラーが出てしまう
        const next = (e as FocusEvent).relatedTarget as Node | null
        const leaving = !next || !(groupOf(t.row)?.rows ?? []).some((r) => r.contains(next))

        validateRow(t.row, touched, leaving)
    })

    // 選択肢・チェック・ファイルは離れる前に値が決まるので、変わった時点で見る
    form.addEventListener('change', (e) => {
        const t = targetOf(e)
        if (!t) return

        touched.add(t.el)
        validateRow(t.row, touched, false)
    })

    form.addEventListener('input', (e) => {
        const t = targetOf(e)
        if (!t) return

        // 組のエラーは先頭の行に出ているので、別の行で打ち始めても消せるように組ごと見る
        const rows = groupOf(t.row)?.rows ?? [t.row]
        if (!rows.some((r) => r.classList.contains('hasError'))) return

        touched.add(t.el)
        validateRow(t.row, touched, true)
    })

    // textarea 以外での Enter による意図しない送信を止める
    form.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') e.preventDefault()
    })

    submitBtn.addEventListener('click', () => {
        if (submitBtn.disabled) return

        clearErrors(form)
        // 送信で出したエラーも、打ち直したときに同じく消えるようにする
        form.querySelectorAll(CONTROLS).forEach((el) => touched.add(el))
        const first = validate(form)

        // 確認中は値を変えられないが、送信の直前にも念のため通す。不備があれば入力フェーズへ戻す
        if (first) {
            setPhase(false)
            scrollToEl(first, true)
            return
        }

        if (!confirming) {
            setPhase(true)
            // 先頭から読み直させたいので、フォームの上端まで戻す
            scrollToEl(root, false)
            return
        }

        submitBtn.disabled = true
        realSubmit.click()
    })

    backBtn?.addEventListener('click', () => {
        if (submitBtn.disabled) return

        setPhase(false)
        scrollToEl(root, false)
    })

    document.addEventListener('wpcf7mailsent', (e) => {
        if (cf7Id && String((e as CustomEvent).detail?.contactFormId ?? '') !== cf7Id) return

        // 完了ページへ送るので、CF7がこのあと書き込む「送信されました」は見せない。
        // CF7は送信直後にフォームをreset()して状態を sent → resetting → sent と付け替え、
        // その resetting のあいだに本文を書く。状態を見るCSSでは取りこぼすので直に伏せる
        form.querySelectorAll<HTMLElement>('.wpcf7-response-output').forEach((out) => {
            out.style.display = 'none'
        })

        window.location.href = thanksUrl
    })

    // サーバー側でNGだった場合はCF7の指摘を同じ見た目で出し直す
    document.addEventListener('wpcf7invalid', (e) => {
        const detail = (e as CustomEvent).detail
        if (cf7Id && String(detail?.contactFormId ?? '') !== cf7Id) return

        unlock()
        clearErrors(form)
        setPhase(false)

        // CF7 6系は into を返さず field（項目名）と idref（入力欄のid）で返す。
        // into だけを見ていると、サーバーが弾いたときに何も出ないまま入力画面へ戻る
        type InvalidField = { into?: string; field?: string; idref?: string; message?: string }

        const fields: InvalidField[] = detail?.apiResponse?.invalid_fields ?? []
        let first: HTMLElement | null = null

        /**
         * 指摘された項目のフィールド行を探す
         * @param field CF7が返した指摘
         * @returns フィールド行。見つからなければ null
         */
        const rowOf = (field: InvalidField): HTMLElement | null => {
            const found =
                (field.into ? form.querySelector(field.into) : null) ??
                (field.field ? form.querySelector(`[data-name="${CSS.escape(field.field)}"]`) : null) ??
                (field.idref ? form.querySelector(`#${CSS.escape(field.idref)}`) : null)

            return found ? getRow(found) : null
        }

        fields.forEach((field) => {
            const row = rowOf(field)
            if (!row) return

            if (!first) first = row
            showError(row, field.message ?? FORMAT_MESSAGE)
        })

        if (first) scrollToEl(first, true)
    })

    // スパム判定・送信失敗はフィールド単位の指摘が無いので、送り直せるようにするだけ
    const failEvents = ['wpcf7spam', 'wpcf7mailfailed']
    failEvents.forEach((evt) => {
        document.addEventListener(evt, unlock)
    })
}

/**
 * ページ内の全フォームを初期化する
 */
const initForms = (): void => {
    document.querySelectorAll<HTMLElement>('[data-form-flow]').forEach(initForm)
}

document.addEventListener('DOMContentLoaded', initForms)
